/**
 * Central Bootstrap Module
 *
 * Calls GET {CENTRAL_URL}/api/suite/switch-context DIRECTLY from the browser.
 * This is NOT proxied through the backend.
 *
 * SUITE STANDARD v2.3.0:
 * - Browser calls Central directly for switch-context
 * - Response is the sole authority for workspace, apps, entitlements
 * - Fail-closed: malformed/missing response blocks all UI
 */

import { getAccessToken } from './centralAuth';
import { BootWorkspace, BootMeta } from '../store/slices/bootSlice';

const RAW_CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL;
const APP_ID = 'levelup-legacy';

/**
 * Fail-closed Central base URL.
 * Trims trailing slash so URL construction never produces double-slashes.
 * Returns null if env var is missing/empty.
 */
function getCentralBase(): string | null {
  if (!RAW_CENTRAL_URL) return null;
  return RAW_CENTRAL_URL.replace(/\/+$/, '');
}

export interface SwitchContextResponse {
  user: {
    id: string;
    email: string;
    name?: string;
    fullName?: string | null;
    avatarUrl?: string | null;
  };
  currentWorkspace: {
    id: string;
    name: string;
    role: string;
    status?: string;
    slug?: string;
    type?: 'personal' | 'business';
    isPrimary?: boolean;
    capabilities?: Record<string, Record<string, Record<string, boolean>>>;
    permissions?: Record<string, boolean>;
  };
  otherWorkspaces: Array<{
    id: string;
    name: string;
    role: string;
    status?: string;
    slug?: string;
    type?: 'personal' | 'business';
    isPrimary?: boolean;
    capabilities?: Record<string, Record<string, Record<string, boolean>>>;
    permissions?: Record<string, boolean>;
  }>;
  apps: Array<{
    appId: string;
    displayName: string;
    webUrl: string;
    iconUrl?: string;
    status: string;
    order: number;
    mobileScheme?: string;
    assets?: { icon?: { iconKey?: string; iconUrl?: string; iconVersion?: number; monochrome?: boolean } };
    ui?: { subtitle?: string; theme?: Record<string, string> };
  }>;
  entitlementsEffective: string[];
  meta?: BootMeta | null;
}

export type SwitchContextResult =
  | { type: 'success'; data: SwitchContextResponse }
  | { type: 'unauthorized' }
  | { type: 'workspace_closed'; workspaceName: string; otherWorkspaces: BootWorkspace[] }
  | { type: 'unavailable'; message: string };

/**
 * Normalizes Central's raw switch-context response into our canonical shape.
 *
 * Central may return "workspace" instead of "currentWorkspace",
 * "other_workspaces" instead of "otherWorkspaces", etc.
 * This function handles all known variants.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeSwitchContext(raw: any): SwitchContextResponse | null {
  const user = raw?.user;
  const currentWorkspace =
    raw?.currentWorkspace ?? raw?.workspace ?? raw?.current_workspace;
  const otherWorkspaces =
    raw?.otherWorkspaces ?? raw?.other_workspaces ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const apps = (raw?.apps ?? []).map((a: any) => ({
    appId: a.appId ?? a.app_id ?? a.id,
    displayName: a.displayName ?? a.display_name ?? a.name ?? a.appId ?? a.id,
    webUrl: a.webUrl ?? a.web_url ?? a.url ?? '',
    iconUrl: a.iconUrl ?? a.icon_url ?? a.icon,
    status: a.status ?? 'active',
    order: typeof a.order === 'number' ? a.order : 999,
    mobileScheme: a.mobileScheme ?? a.mobile_scheme,
    // Preserve extended fields for shell adapter (v1.1 icon/theme resolution)
    ...(a.assets ? { assets: a.assets } : {}),
    ...(a.ui ? { ui: a.ui } : {}),
  }));
  const entitlementsEffective =
    raw?.entitlementsEffective ?? raw?.entitlements_effective ?? [];

  const meta = raw?.meta ?? null;

  // Fail-closed: workspace ID is required
  if (!currentWorkspace?.id) {
    console.error(
      '[switch-context] Normalization failed — no workspace.id. Raw top-level keys:',
      Object.keys(raw ?? {})
    );
    return null;
  }

  console.log('[switch-context] normalized currentWorkspace.id:', currentWorkspace.id);

  // Normalize user — preserve fullName/avatarUrl/platform flags if Central provides them
  const normalizedUser = user
    ? {
        id: user.id ?? '',
        email: user.email ?? '',
        name: user.name ?? user.fullName ?? user.full_name,
        fullName: user.fullName ?? user.full_name ?? user.name ?? null,
        avatarUrl: user.avatarUrl ?? user.avatar_url ?? null,
        platformInternal: user.platformInternal === true || user.platform_internal === true,
        platformSuperAdmin: user.platformSuperAdmin === true || user.platform_super_admin === true,
      }
    : { id: '', email: '' };

  // Normalize workspaces — preserve all Central fields for shell adapter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizeWorkspace = (ws: any) => ({
    id: ws.id,
    name: ws.name,
    role: ws.role,
    status: ws.status,
    ...(ws.slug ? { slug: ws.slug } : {}),
    ...(ws.type ? { type: ws.type } : {}),
    ...(typeof ws.isPrimary === 'boolean' ? { isPrimary: ws.isPrimary } : {}),
    ...(ws.capabilities ? { capabilities: ws.capabilities } : {}),
    ...(ws.permissions ? { permissions: ws.permissions } : {}),
  });

  return {
    user: normalizedUser,
    currentWorkspace: normalizeWorkspace(currentWorkspace),
    otherWorkspaces: Array.isArray(otherWorkspaces)
      ? otherWorkspaces.map(normalizeWorkspace)
      : [],
    apps: Array.isArray(apps) ? apps : [],
    entitlementsEffective: Array.isArray(entitlementsEffective)
      ? entitlementsEffective
      : [],
    meta,
  };
}

export async function fetchSwitchContext(): Promise<SwitchContextResult> {
  const token = getAccessToken();
  if (!token) {
    return { type: 'unauthorized' };
  }

  const centralBase = getCentralBase();
  if (!centralBase) {
    return { type: 'unavailable', message: 'VITE_CENTRAL_URL not configured' };
  }

  // Always construct absolute URL — never relative
  const url = new URL('/api/suite/switch-context', centralBase).toString();

  console.log('[switch-context] fetching:', url);

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-App-Id': APP_ID,
        Accept: 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (response.status === 401 || response.status === 403) {
      return { type: 'unauthorized' };
    }

    if (response.status === 409) {
      const body = await response.json().catch(() => ({}));
      const workspaceName =
        body?.workspace?.name ?? body?.workspaceName ?? 'your workspace';
      const otherWorkspaces: BootWorkspace[] = Array.isArray(body?.otherWorkspaces)
        ? body.otherWorkspaces
        : [];
      return { type: 'workspace_closed', workspaceName, otherWorkspaces };
    }

    if (!response.ok) {
      return {
        type: 'unavailable',
        message: `Central returned ${response.status}`,
      };
    }

    const raw = await response.json();

    // Normalize: Central may use "workspace" instead of "currentWorkspace"
    const data = normalizeSwitchContext(raw);
    if (!data) {
      return {
        type: 'unavailable',
        message: 'Malformed response from Central (missing required fields)',
      };
    }

    return { type: 'success', data };
  } catch (err) {
    clearTimeout(timeout);
    if (err instanceof Error && err.name === 'AbortError') {
      return { type: 'unavailable', message: 'Central request timed out' };
    }
    return {
      type: 'unavailable',
      message: err instanceof Error ? err.message : 'Network error reaching Central',
    };
  }
}
